import {
  breakpointSizes,
  ErrorList,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { createResetErrorsAction } from 'src/app/AccountSettings/redux/accountSettings.actions'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { Issuers } from 'src/enums'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type CardInformationBoxFlatFields = {
  expires: string
  cvc: string
}

interface CardInformationBoxProps {
  className?: string
  hasEditPermissions: boolean | undefined
  creditCard: CreditCardListModel
  puttingCreditCard: boolean
  patchingCreditCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  onSuccessfulSubmit: (flatFields: CardInformationBoxFlatFields) => void
  hideExpirationDate?: boolean
}

const CardInformationBox: React.FC<CardInformationBoxProps> = (
  props,
): React.ReactElement => {
  const [isEditing, setIsEditing] = useState<boolean>(false)

  const dispatch = useDispatch()

  const initialFlatFields: CardInformationBoxFlatFields = {
    expires: props.creditCard.expiresTimezonedString,
    cvc: '',
  }
  const flatFieldsValidation =
    (): FormFieldsValidation<CardInformationBoxFlatFields> => ({
      expires: [stringIsEmpty()],
      cvc: [stringIsEmpty()],
    })
  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<CardInformationBoxFlatFields>(
    initialFlatFields,
    flatFieldsValidation,
    props.errorDetailMap,
  )

  const formatExpires = (val: string): string => {
    const onlyNums = val.replace(/[^\d]/g, '')
    if (onlyNums.length < 3) {
      return onlyNums
    } else if (onlyNums.length === 3) {
      return `${onlyNums.slice(0, 1)}/${onlyNums.slice(1, 3)}`
    } else {
      return `${onlyNums.slice(0, 2)}/${onlyNums.slice(2, Math.min(onlyNums.length, 4))}`
    }
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    dispatch(createResetErrorsAction())

    const flatFieldsPassedValidation = flatFieldsSetErrors()
    if (flatFieldsPassedValidation) {
      const formattedExpiry = moment(flatFields.expires, 'MM/YY', true)
        .endOf('month')
        .format('YYYY-MM-DD')
      props.onSuccessfulSubmit({
        ...flatFields,
        expires: moment(flatFields.expires, 'MM/YY', true).isValid()
          ? formattedExpiry
          : '2000-01-01',
      })
    }
  }

  return (
    <Box
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
      noPadding
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          padding: 36px 40px;
          ${getBreakpointOnly(breakpointSizes.XXSM)(css`
            padding: 36px 20px;
          `)}
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                20,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            <Trns source={'Card Information'} />
          </span>
          {props.hasEditPermissions &&
            !isEditing &&
            !props.creditCard.isClosed && (
              <HoverableLink
                css={css`
                  display: flex;
                  align-items: center;
                `}
                onClick={(): void => {
                  setIsEditing(true)
                  dispatch(createResetErrorsAction())
                }}
                ariaLabelSource={`Update`}
              >
                <Trns source={'Update'} />
                <SvgUpdate
                  css={css`
                    transform: scale(calc(14 / 24));
                  `}
                />
              </HoverableLink>
            )}
        </div>
        <div
          css={css`
            margin-top: 30px;
            display: flex;
            flex-direction: column;
          `}
        >
          <FieldLabel>
            <Trns source={'Card or Bank Account'} />
          </FieldLabel>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              margin-top: 10px;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <img
                css={css`
                  height: 20px;
                `}
                src={props.creditCard.cardImage.urls.small}
                alt={''}
              />
              <span
                css={css`
                  margin-left: 8px;
                  margin-right: 12px;
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )}
                `}
              >
                {props.creditCard.issuerName}
              </span>
              <Tag text={props.creditCard.last4} size={TagSizes.Small} />
            </div>
            {!isEditing && props.creditCard.issuer !== Issuers.Comdata && (
              <div>
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )}
                  `}
                >
                  <Trns
                    source={'Expiration Date: ${date}'}
                    expressions={{
                      date: (
                        <span
                          css={css`
                            ${getFontCss(
                              14,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )}
                          `}
                        >
                          {props.creditCard.expiresTimezonedString}
                        </span>
                      ),
                    }}
                  />
                </span>
              </div>
            )}
          </div>
          {isEditing && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <form onSubmit={onSubmit}>
                <div
                  css={css`
                    margin-top: 24px;
                    display: flex;
                    flex-direction: row;
                    gap: 20px;
                  `}
                >
                  {/* redundant check here but including it for extra security */}
                  {props.creditCard.issuer !== Issuers.Comdata && (
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        width: 50%;
                      `}
                    >
                      <FieldLabel>
                        <Trns source={'Expiration Date'} />
                      </FieldLabel>
                      <TextInputField
                        errors={flatFieldsErrors['expires']}
                        design={TextInputFieldDesigns.New}
                        value={flatFields['expires']}
                        showUpdates
                        baseValue={props.creditCard.expiresTimezonedString}
                        onChange={(val: string): void => {
                          onFlatFieldChange('expires', formatExpires(val))
                        }}
                        placeholder={'MM/YY'}
                      />
                    </div>
                  )}
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      width: 50%;
                    `}
                  >
                    <FieldLabel>
                      <Trns source={'CVC'} />
                    </FieldLabel>
                    <TextInputField
                      errors={flatFieldsErrors['cvc']}
                      design={TextInputFieldDesigns.New}
                      value={flatFields['cvc']}
                      showUpdates
                      baseValue={''}
                      onChange={(val: string): void => {
                        onFlatFieldChange('cvc', val)
                      }}
                      placeholder={'3 or 4 digits'}
                    />
                  </div>
                </div>
                {props.topLevelError && (
                  <div
                    css={css`
                      margin-top: 5px;
                    `}
                  >
                    <ErrorList errors={[props.topLevelError]} />
                  </div>
                )}
                <div
                  css={css`
                    ${props.topLevelError
                      ? css`
                          margin-top: 15px;
                        `
                      : css`
                          margin-top: 40px;
                        `}
                    display: flex;
                    flex-direction: row;
                    gap: 20px;
                    align-items: center;
                  `}
                >
                  <Button
                    type={ButtonTypes.submit}
                    design={ButtonDesigns.designCta}
                    text={<Trns source={'Save Updates'} />}
                    loading={props.patchingCreditCard}
                    disabled={props.puttingCreditCard}
                  />
                  <HoverableLink
                    onClick={(): void => {
                      setIsEditing(false)
                      dispatch(createResetErrorsAction())
                    }}
                    ariaLabelSource={'Cancel'}
                  >
                    <Trns source={'Cancel'} />
                  </HoverableLink>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </Box>
  )
}

export default CardInformationBox
